import { TimeoutError } from '@ibe/errors';
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_MS = 1_000;
const DEFAULT_MAX_DELAY_MS = 30_000;
const BACKOFF_FACTOR = 2;
/** Resolves after `ms`, rejecting early if the signal aborts. */
export function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted === true) {
            reject(new TimeoutError(undefined, { message: 'Aborted before sleeping' }));
            return;
        }
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        function onAbort() {
            clearTimeout(timer);
            reject(new TimeoutError(undefined, { message: 'Aborted while sleeping' }));
        }
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}
/**
 * Delay before a given attempt: exponential, capped, optionally jittered.
 *
 * Exported so retry behaviour can be asserted directly rather than inferred
 * from wall-clock timings in a flaky test.
 */
export function backoffDelay(attempt, baseDelayMs = DEFAULT_BASE_DELAY_MS, maxDelayMs = DEFAULT_MAX_DELAY_MS, jitter = true) {
    const exponential = Math.min(baseDelayMs * BACKOFF_FACTOR ** (attempt - 1), maxDelayMs);
    if (!jitter)
        return exponential;
    // Full jitter: uniform over [0, exponential]. Spreads a thundering herd
    // better than the equal-jitter and decorrelated variants at this scale.
    const max = Math.max(1, Math.floor(exponential));
    const arr = new Uint32Array(1);
    globalThis.crypto.getRandomValues(arr);
    return (arr[0] ?? 0) % (max + 1);
}
/**
 * Runs `operation`, retrying transient failures.
 *
 * Rethrows the last error once attempts are exhausted, rather than wrapping
 * it: callers need the original failure to decide what to do next.
 */
export async function retry(operation, options = {}) {
    const { maxAttempts = DEFAULT_MAX_ATTEMPTS, baseDelayMs = DEFAULT_BASE_DELAY_MS, maxDelayMs = DEFAULT_MAX_DELAY_MS, jitter = true, isRetryable = () => true, onRetry, signal, } = options;
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            return await operation(attempt);
        }
        catch (error) {
            lastError = error;
            const exhausted = attempt >= maxAttempts;
            if (exhausted || !isRetryable(error))
                throw error;
            const delayMs = backoffDelay(attempt, baseDelayMs, maxDelayMs, jitter);
            onRetry?.(attempt, delayMs, error);
            await sleep(delayMs, signal);
        }
    }
    throw lastError;
}
/** Rejects with a TimeoutError if `operation` outlives `timeoutMs`. */
export async function withTimeout(operation, timeoutMs, message = 'Operation timed out') {
    const controller = new AbortController();
    // The controller is aborted only by this timer, so `signal.aborted` is
    // itself the record of whether we timed out — no parallel flag to keep
    // in step with it.
    const timer = setTimeout(() => {
        controller.abort();
    }, timeoutMs);
    try {
        return await Promise.race([
            operation(controller.signal),
            new Promise((_resolve, reject) => {
                controller.signal.addEventListener('abort', () => {
                    reject(new TimeoutError(undefined, { message, metadata: { timeoutMs } }));
                }, { once: true });
            }),
        ]);
    }
    catch (error) {
        // An operation that cooperates with the abort signal rejects on its own
        // before the timeout branch can win the race, which would surface an
        // "aborted" error to a caller who asked about a timeout. Translate it:
        // the reason the operation aborted is that we timed it out.
        if (controller.signal.aborted) {
            throw new TimeoutError(undefined, { message, metadata: { timeoutMs }, cause: error });
        }
        throw error;
    }
    finally {
        clearTimeout(timer);
    }
}
//# sourceMappingURL=retry.js.map